import type {
  AdminScopeAccess,
  EntitlementChange,
  OrganizationEntitlement,
  OrganizationEntitlementOverview,
} from '@app/src/types/entitlements'
import {
  AccessPolicy,
  EntitlementEffect,
  EntitlementFeature,
  EntitlementSource,
  EntitlementState,
} from '@app/src/types/entitlements'
import { SolutionType } from '@app/src/types/resourceExplorer'

export enum CellStatus {
  Included = 'Included',
  Granted = 'Granted',
  Locked = 'Locked',
  Denied = 'Denied',
  Hidden = 'Hidden',
  NotApplicable = 'NotApplicable',
}

export interface AccessCell {
  scope: AdminScopeAccess
  status: CellStatus
  reason: string
  usageWarning?: string
  rule?: OrganizationEntitlement
}

export interface AccessRow {
  id: number
  rowId: string
  title: string
  isAssessment: boolean
  isDefaultScope: boolean
  questionnaire?: AccessCell
  aiScreening?: AccessCell
  assessment?: AccessCell
  subRows?: AccessRow[]
}

export const featureLabel: Record<EntitlementFeature, string> = {
  [EntitlementFeature.Questionnaire]: 'questionnaires',
  [EntitlementFeature.Assessment]: 'assessments',
  [EntitlementFeature.AiScreening]: 'AI screening',
}

export const policyLabel: Record<AccessPolicy, string> = {
  [AccessPolicy.Included]: 'Included',
  [AccessPolicy.AddOn]: 'Add-on',
  [AccessPolicy.Hidden]: 'Hidden',
}

const migratedReason: Record<Exclude<EntitlementSource, EntitlementSource.Csm>, string> = {
  [EntitlementSource.MigratedFlag]: 'Migrated from LaunchDarkly',
  [EntitlementSource.MigratedAvailability]: 'Migrated from template availability',
}

const usageWarning: Partial<Record<EntitlementState, string>> = {
  [EntitlementState.OffByAdmin]: 'Customer has it switched off',
  [EntitlementState.LimitReached]: 'No credits left',
}

const cellStatus = (scope: AdminScopeAccess, rule: OrganizationEntitlement | undefined): CellStatus => {
  if (scope.state === EntitlementState.NotApplicable) return CellStatus.NotApplicable
  if (scope.isEntitled) return rule ? CellStatus.Granted : CellStatus.Included
  if (rule) return CellStatus.Denied
  return scope.policy === AccessPolicy.Hidden ? CellStatus.Hidden : CellStatus.Locked
}

export const ruleSourceLabel = ({ source, effect }: Pick<OrganizationEntitlement, 'source' | 'effect'>): string => {
  if (source === EntitlementSource.Csm) return effect === EntitlementEffect.Grant ? 'Granted by CS' : 'Denied by CS'
  return migratedReason[source]
}

export const untilLabel = (validUntil: string) => `until ${validUntil.slice(0, 10)}`

const ruleOrigin = (rule: OrganizationEntitlement): string =>
  rule.scopeId === undefined ? `Rule for all ${featureLabel[rule.feature]}` : ruleSourceLabel(rule)

const cellReason = (scope: AdminScopeAccess, rule: OrganizationEntitlement | undefined): string => {
  if (scope.state === EntitlementState.NotApplicable) return 'Not available for this solution'
  if (!rule) return `Template default: ${policyLabel[scope.policy]}`
  const origin = ruleOrigin(rule)
  return rule.validUntil ? `${origin} · ${untilLabel(rule.validUntil)}` : origin
}

export const toCell = (scope: AdminScopeAccess, entitlements: OrganizationEntitlement[]): AccessCell => {
  const rule = entitlements.find(entitlement => entitlement.id === scope.decidedByEntitlementId)
  return {
    scope,
    status: cellStatus(scope, rule),
    reason: cellReason(scope, rule),
    usageWarning: usageWarning[scope.state],
    rule,
  }
}

const entitledCustomerSees: Partial<Record<EntitlementState, string>> = {
  [EntitlementState.OffByAdmin]: 'Visible, but switched off by the customer.',
  [EntitlementState.LimitReached]: 'Visible, but blocked until they get more AI screening credits.',
}

export const customerSees = ({ scope, status }: AccessCell): string => {
  if (!scope.isVisible) return 'Not shown at all.'
  if (scope.isEntitled) return entitledCustomerSees[scope.state] ?? 'Visible and usable.'
  return status === CellStatus.Locked
    ? 'Visible as an add-on they can ask for.'
    : 'Visible as unavailable, with a neutral message.'
}

export const isDecidedByFeatureRule = (cell: AccessCell): boolean =>
  cell.rule !== undefined && cell.rule.scopeId === undefined

const hasOrganizationRule = (row: AccessRow): boolean =>
  [row.questionnaire, row.aiScreening, row.assessment].some(cell => cell?.rule?.scopeId !== undefined)

const keepOrganizationRules = (rows: AccessRow[]): AccessRow[] =>
  rows.flatMap(row => {
    const subRows = keepOrganizationRules(row.subRows ?? [])
    return hasOrganizationRule(row) || subRows.length ? [{ ...row, subRows }] : []
  })

const assessmentRow = (cell: AccessCell): AccessRow => ({
  id: cell.scope.scopeId,
  rowId: `assessment-${cell.scope.scopeId}`,
  title: cell.scope.title,
  isAssessment: true,
  isDefaultScope: cell.scope.isDefaultScope,
  assessment: cell,
})

export const accessRows = (overview: OrganizationEntitlementOverview, onlyOrganizationRules: boolean): AccessRow[] => {
  const cells = overview.scopes.map(scope => toCell(scope, overview.entitlements))
  const byFeature = Object.groupBy(cells, cell => cell.scope.feature)
  const assessmentsByQuestionnaire = Object.groupBy(
    byFeature[EntitlementFeature.Assessment] ?? [],
    cell => cell.scope.questionnaireTemplateId ?? 0,
  )
  const screeningByTemplate = Object.groupBy(
    byFeature[EntitlementFeature.AiScreening] ?? [],
    cell => cell.scope.scopeId,
  )

  const questionnaireRows = (byFeature[EntitlementFeature.Questionnaire] ?? []).map(
    (cell): AccessRow => ({
      id: cell.scope.scopeId,
      rowId: `questionnaire-${cell.scope.scopeId}`,
      title: cell.scope.title,
      isAssessment: false,
      isDefaultScope: cell.scope.isDefaultScope,
      questionnaire: cell,
      aiScreening: screeningByTemplate[cell.scope.scopeId]?.[0],
      subRows: (assessmentsByQuestionnaire[cell.scope.scopeId] ?? []).map(assessmentRow),
    }),
  )
  const questionnaireIds = new Set(questionnaireRows.map(row => row.id))
  const assessmentsWithoutQuestionnaire = (byFeature[EntitlementFeature.Assessment] ?? [])
    .filter(cell => !questionnaireIds.has(cell.scope.questionnaireTemplateId ?? 0))
    .map(assessmentRow)

  const rows = [...questionnaireRows, ...assessmentsWithoutQuestionnaire]
  return onlyOrganizationRules ? keepOrganizationRules(rows) : rows
}

export const findRule = (
  entitlements: OrganizationEntitlement[],
  feature: EntitlementFeature,
  scopeId?: number,
): OrganizationEntitlement | undefined =>
  entitlements.find(
    entitlement =>
      entitlement.feature === feature && entitlement.scopeId === scopeId && entitlement.solutionType === undefined,
  )

export const templateGrantCount = (overview: OrganizationEntitlementOverview, feature: EntitlementFeature): number =>
  overview.scopes
    .filter(scope => scope.feature === feature && scope.state !== EntitlementState.NotApplicable)
    .map(scope => toCell(scope, overview.entitlements).rule)
    .filter(rule => rule?.scopeId !== undefined && rule.effect === EntitlementEffect.Grant).length

export const grantAllChanges = (scopes: AdminScopeAccess[], feature: EntitlementFeature): EntitlementChange[] =>
  scopes
    .filter(scope => scope.feature === feature && scope.isVisible && !scope.isEntitled)
    .map(scope => ({
      feature,
      scopeId: scope.scopeId,
      effect: EntitlementEffect.Grant,
      note: `Grant all ${featureLabel[feature]}`,
    }))

export const featureWideChange = (
  feature: EntitlementFeature,
  effect: EntitlementEffect | null,
): EntitlementChange => ({
  feature,
  scopeId: null,
  effect,
  note: effect === EntitlementEffect.Deny ? `Deny all ${featureLabel[feature]}` : null,
})

export const accessSolutions = (assignedSolutionTypes: string[]): SolutionType[] =>
  Object.values(SolutionType).filter(
    solutionType => solutionType !== SolutionType.Transparency && assignedSolutionTypes.includes(solutionType),
  )
